<div class="main-container" [formGroup]="patientDetailMainInfo" class="p-6">
  <div class="grid grid-cols-6 gap-2" [formGroup]="patientDetailMainInfo">
    <mat-form-field appearance="outline" class="col-span-2">
      <mat-label>First name</mat-label>
      <input matInput required formControlName="firstName" />
      <mat-error
        *ngIf="!patientDetailMainInfo.controls['firstName'].errors?.['pattern']"
        >{{ "Required field" }}</mat-error
      >
      <mat-error
        *ngIf="patientDetailMainInfo.controls['firstName'].errors?.['pattern']"
        >{{ "Invalid name" }}</mat-error
      >
    </mat-form-field>

    <mat-form-field appearance="outline" class="col-span-2">
      <mat-label>Middle name</mat-label>
      <input matInput formControlName="middleName" />
      <!-- <mat-error>{{'Required field'}}</mat-error> -->
      <mat-error *ngIf="patientDetailMainInfo.controls['middleName']?.errors">{{
        "Invalid name"
      }}</mat-error>
    </mat-form-field>

    <mat-form-field appearance="outline" class="col-span-2">
      <mat-label>Last name</mat-label>
      <input matInput required formControlName="lastName" />
      <mat-error
        *ngIf="!patientDetailMainInfo.controls['lastName'].errors?.['pattern']"
        >{{ "Required field" }}</mat-error
      >
      <mat-error
        *ngIf="patientDetailMainInfo.controls['lastName'].errors?.['pattern']"
        >{{ "Invalid name" }}</mat-error
      >
    </mat-form-field>

    <mat-form-field appearance="outline" class="col-span-2">
      <mat-label>Patient ID</mat-label>
      <input matInput required formControlName="patientId" />
      <!--
        Shown only while the server is being asked, so a field that stays silent
        for a moment reads as "still checking" rather than "accepted".
      -->
      <mat-spinner
        *ngIf="isChecking('patientId')"
        matSuffix
        diameter="16"
        class="mr-2"
      ></mat-spinner>
      <!--
        Answered as the ID is typed rather than waiting for the save to be
        refused. Takes precedence over the rejected-save message below, which is
        the same objection arriving later and by a slower route.
      -->
      <mat-error *ngIf="isTaken('patientId')">{{
        "Already in use"
      }}</mat-error>
      <!--
        The API's objection about this specific value. It replaces the
        generic validation messages rather than joining them: those describe
        the shape of the value, and the value here is well-formed but refused.
      -->
      <mat-error *ngIf="!isTaken('patientId') && hasServerError('patientId')">{{
        serverErrorFor('patientId')
      }}</mat-error>
      <!--
        Each message repeats the server-error guard instead of sharing a wrapper.
        mat-form-field picks its errors out of projected content by selector and
        only direct children match, so nesting these in an ng-container drops them
        into the default slot, where they render inside the input box.

        Keyed off the `required` error rather than "no pattern error", which was
        true whenever the field was fine and put a red "Required field" under
        valid values. Red has to mean something for the server-rejection message
        above to be legible.
      -->
      <mat-error
        *ngIf="
          !isTaken('patientId') &&
          !hasServerError('patientId') &&
          patientDetailMainInfo.controls['patientId'].errors?.['required']
        "
        >{{ "Required field" }}</mat-error
      >
      <mat-error
        *ngIf="
          !isTaken('patientId') &&
          !hasServerError('patientId') &&
          patientDetailMainInfo.controls['patientId'].errors?.['pattern']
        "
        >{{ "Invalid patientId" }}</mat-error
      >
    </mat-form-field>

    <app-date-picker
      class="col-span-2"
      label="Date of birth"
      formControlName="dob"
      [maxDate]="currDate"
      [dateOnly]="true"
    >
    </app-date-picker>

    <mat-form-field appearance="outline" class="col-span-2">
      <mat-label>Age</mat-label>
      <input
        [(ngModel)]="age"
        readonly="!editable"
        matInput
        formControlName="age"
      />
      <mat-error>{{ "Invalid age" }}</mat-error>
    </mat-form-field>

    <div class="col-span-6">
      <!-- Changed from col-span-3 to col-span-6 -->
      <div class="grid grid-cols-4 gap-2">
        <mat-form-field appearance="outline">
          <mat-label>{{ "Gender" }}</mat-label>
          <mat-select formControlName="gender">
            <mat-option *ngFor="let role of gender" [value]="role.value">
              {{ role.viewValue }}
            </mat-option>
          </mat-select>
          <mat-error>{{ "Required field" }}</mat-error>
        </mat-form-field>

        <app-weight-input
          [weightInKg]="patientDetailMainInfo.controls['weight'].value"
          (weightChange)="onWeightChange($event)"
          formControlName="weight"
        ></app-weight-input>

        <app-height-input
          [heightInCm]="patientDetailMainInfo.controls['height'].value"
          (heightChange)="onHeightChange($event)"
          formControlName="height"
        ></app-height-input>

        <mat-form-field appearance="outline">
          <mat-label>BMI</mat-label>
          <input
            matInput
            [ngModel]="bmi"
            readonly="!editable"
            formControlName="bmi"
          />
          <mat-error>{{ "Required field" }}</mat-error>
        </mat-form-field>
      </div>
    </div>

    <!--
      Ahead of Email on purpose. The department decides whether the IRB form may
      be offered at all, and ticking that option is what makes an email address
      mandatory — so the answer has to be settled before the user reaches the
      field it applies to.

      Dropped entirely for a user tied to one department rather than shown greyed
      out. There is no choice to present them: their own department is the only
      possible answer, it is already on the form, and a permanently dead field is
      just a thing to read past on a form that is long enough already. The value
      is set in ngOnInit and published from the form control, so nothing
      downstream depends on this field being on screen.
    -->
    <app-department-select
      *ngIf="!isDepartmentDisabled"
      class="col-span-6"
      [selectedValue]="patientDetailMainInfo.value.departmentId"
      (departmentSelected)="onDepartmentChange($event)"
    >
    </app-department-select>

    <!--
      After the department, because the department decides whether Dormo Direct
      is offered at all; and before Email and Phone, which a Direct patient needs.
    -->
    <div
      *ngIf="directEnabled"
      class="col-span-6 patient-type"
      role="radiogroup"
      aria-label="Patient type"
    >
      <button
        type="button"
        role="radio"
        class="patient-type__option"
        [class.patient-type__option--selected]="!isDirect"
        [attr.aria-checked]="!isDirect"
        (click)="setPatientType('CLINIC')"
      >
        <mat-icon class="patient-type__icon">local_hospital</mat-icon>
        <span class="patient-type__body">
          <span class="patient-type__title">Clinic Patient</span>
          <span class="patient-type__subtitle">Patient is managed by your clinic</span>
          <span class="patient-type__point">Device assigned from your inventory</span>
          <span class="patient-type__point">Clinic manages the patient and test</span>
        </span>
      </button>
      <button
        type="button"
        role="radio"
        class="patient-type__option"
        [class.patient-type__option--selected]="isDirect"
        [attr.aria-checked]="isDirect"
        (click)="setPatientType('DIRECT')"
      >
        <mat-icon class="patient-type__icon">local_shipping</mat-icon>
        <span class="patient-type__body">
          <span class="patient-type__title">Direct Patient</span>
          <span class="patient-type__badge">Dormotech handles shipping and support</span>
          <span class="patient-type__point">Patient details are sent directly to Dormotech</span>
          <span class="patient-type__point">Dormotech ships the device to the patient</span>
          <span class="patient-type__point">Dormotech handles support and returns</span>
          <span class="patient-type__point">You can track test status in your account</span>
        </span>
      </button>
    </div>
    <p *ngIf="!directEnabled && directUnavailable" class="col-span-6 direct-unavailable" role="status">
      <mat-icon class="direct-unavailable__icon">info_outline</mat-icon>
      <span>{{ directUnavailable }}</span>
    </p>

    <mat-form-field appearance="outline" class="col-span-3">
      <mat-label>Email</mat-label>
      <input matInput formControlName="email" />
      <!--
        The API's objection about this specific value. It replaces the
        generic validation messages rather than joining them: those describe
        the shape of the value, and the value here is well-formed but refused.
      -->
      <mat-error *ngIf="hasServerError('email')">{{
        serverErrorFor('email')
      }}</mat-error>
      <!--
        Each message carries the server-error guard itself instead of sharing a
        wrapper. mat-form-field picks its errors out of projected content by
        selector, and only direct children match: nested inside an ng-container
        they fall through to the default slot and render inside the input box.

        Exactly one of the three below can match at a time, which is the point:
        the strip beneath a control is one line tall, so a second message
        rendered alongside the first spills out of the field and lands on top of
        the row underneath.
      -->
      <mat-error
        *ngIf="
          !hasServerError('email') &&
          patientDetailMainInfo.controls['email'].errors?.['required'] &&
          patientDetailMainInfo.value.sendResearchConsent
        "
        >{{ "Required for the IRB form" }}</mat-error
      >
      <!--
        Keyed off the `required` error rather than "no pattern error", which was
        true whenever the field was fine and put a red "Required field" under
        valid values. Red has to mean something for the server-rejection message
        above to be legible.

        The consent option is what makes the field mandatory when it is ticked,
        and it has its own message above, so this one stands down for that case
        rather than stacking a second line under the same field.
      -->
      <mat-error
        *ngIf="
          !hasServerError('email') &&
          patientDetailMainInfo.controls['email'].errors?.['required'] &&
          !patientDetailMainInfo.value.sendResearchConsent
        "
        >{{ isDirect ? "Required for a Direct patient" : "Required field" }}</mat-error
      >
      <!--
        Both keys, because the field is validated by Angular's email validator,
        which reports under `email` — `pattern` alone left a malformed address
        with no message at all while the step quietly refused to advance, so the
        user had nothing to tell them what to fix.
      -->
      <mat-error
        *ngIf="
          !hasServerError('email') &&
          (patientDetailMainInfo.controls['email'].errors?.['email'] ||
            patientDetailMainInfo.controls['email'].errors?.['pattern'])
        "
        >{{ "Invalid email" }}</mat-error
      >
    </mat-form-field>

    <mat-form-field appearance="outline" class="col-span-3">
      <mat-label>Phone</mat-label>
      <input matInput formControlName="phone" [required]="isDirect" />
      <mat-error *ngIf="patientDetailMainInfo.controls['phone'].errors?.['required']">{{
        "Required for a Direct patient"
      }}</mat-error>
      <mat-error *ngIf="patientDetailMainInfo.controls['phone'].errors?.['phone']">{{ phoneFormatMessage }}</mat-error>
    </mat-form-field>

    <app-date-time-picker
      class="col-span-2"
      label="Planned Date"
      formControlName="scheduleDateTime"
      [minDate]="currDate"
      [required]="true"
    ></app-date-time-picker>

    <mat-form-field appearance="outline" class="col-span-2">
      <mat-label>Test Type</mat-label>
      <mat-select formControlName="testType">
        <mat-option *ngFor="let t of allTypeOptions" [value]="t.value">
          {{ t.viewValue }}
        </mat-option>
      </mat-select>
      <mat-error>{{ "Required field" }}</mat-error>
    </mat-form-field>

    <mat-form-field appearance="outline" class="col-span-2">
      <mat-label>Test Location</mat-label>
      <mat-select formControlName="location">
        <mat-option *ngFor="let t of allLocationOptions" [value]="t.value">
          {{ t.viewValue }}
        </mat-option>
      </mat-select>
      <mat-error>{{ "Required field" }}</mat-error>
    </mat-form-field>

    <mat-form-field appearance="outline" class="col-span-2" *ngIf="isTitration">
      <mat-label>Titration Type</mat-label>
      <mat-select formControlName="titrationType">
        <mat-option *ngFor="let t of titrationTypeOptions" [value]="t.value">
          {{ t.viewValue }}
        </mat-option>
      </mat-select>
    </mat-form-field>

    <!--
      Dynamic subscript because the over-the-cap message is two sentences in a
      field a third of the form wide. The default strip under a control is one
      line tall and absolutely positioned, so the wrapped lines spilled out of it
      and over the checkboxes below; sized to its content, the strip pushes the
      next row down instead. Held to the top of its cell, since with no strip
      reserved a stretched field grows its box to the height of its neighbour's.
    -->
    <!-- Not for a Direct patient: they get exactly one test, sent with them to Dormotech Direct. -->
    <mat-form-field
      *ngIf="!isDirect"
      appearance="outline"
      class="col-span-2 self-start"
      subscriptSizing="dynamic"
    >
      <mat-label>Number of Tests</mat-label>
      <input
        type="number"
        min="1"
        [max]="maxTestsPerOpening"
        matInput
        formControlName="testsCount"
        noNegativeInput
      />
      <mat-error *ngIf="patientDetailMainInfo.controls['testsCount'].errors?.['min']">
        Can be minimum 1
      </mat-error>
      <mat-error *ngIf="patientDetailMainInfo.controls['testsCount'].errors?.['max']">
        {{ maxTestsPerOpeningError }}
      </mat-error>
    </mat-form-field>
    <app-test-rooms-select
      *ngIf="patientDetailMainInfo.value.location == 'Lab' && !isDirect"
      class="col-span-2"
      [required]="false"
      [selectedValue]="patientDetailMainInfo.value.roomId"
      (departmentSelected)="onTestRoomChange($event)"
    ></app-test-rooms-select>
    <app-users-select
      label="Interpreting Physician"
      class="col-span-2"
      [role]="'PHYSICIAN,SHARED_PHYSICIAN'"
      [includeUnassigned]="true"
      [selectedValue]="patientDetailMainInfo.value.interpretingPhysicianId"
      (userSelected)="onInterpretingPhysicianIdUserChange($event)"
    ></app-users-select>
    <!--
      One stack rather than three grid rows: the toggles belong together, and
      grouping them lets the row spacing be set once instead of inherited from
      the grid gap.

      `items-start` keeps each checkbox as narrow as its own label, so its state
      layer and click target do not span the full width. That sizes every child
      to its content though, which a run of prose is happy to take right past the
      edge of the dialog — so the blocks of text below opt back in to the
      container's width with `self-stretch` and wrap inside it.
    -->
    <div class="col-span-6 mt-2 patient-flags flex flex-col items-start gap-1">
      <div class="flex items-center gap-1">
        <mat-checkbox formControlName="isPediatric" (change)="onPediatricChange()">
          Pediatric Interpretation
        </mat-checkbox>
        <mat-icon class="info-icon" [matTooltip]="pediatricHint">
          help_outline
        </mat-icon>
      </div>
      <!-- Indent matches the denser state layer, so the warning lines up under
           the label rather than under the box. -->
      <p
        *ngIf="pediatricWarningVisible"
        class="pediatric-warning text-sm ml-7 self-stretch"
      >
        &#9888;&#65039; {{ pediatricWarning }}
      </p>
      <mat-checkbox formControlName="hasExternalFile">
        Additional Medical History details in an external file
      </mat-checkbox>
      <div *ngIf="researchConsentEnabled" class="self-stretch">
        <mat-checkbox formControlName="sendResearchConsent">
          Send IRB form
        </mat-checkbox>
        <!-- Indent matches the denser state layer, so the hint lines up under
             the label rather than under the box. -->
        <p class="text-sm text-muted mt-1 ml-7">
          Emails the patient the research participation consent form once they
          are saved. An email address is required.
        </p>
      </div>
    </div>
  </div>

  <!--
    A Direct patient's shipping address: on this step, with everything else the
    Direct lab needs, and Next waits for it to be validated. Hidden rather than
    removed, so the address typed and validated survives a change of patient type.
  -->
  <div class="mt-4" [hidden]="!isDirect">
    <app-direct-shipping-address></app-direct-shipping-address>
  </div>
</div>
